import { PlatformError, type DocTxOps } from '@djgg/core';
import { buildJobDoc } from './paths.js';
import { assertQualityClaim } from './qualityStateMachine.js';
import type { QualityClaimV1, QualityRunStateV2 } from './qualitySchemas.js';
import type { BuildJob } from './types.js';

export interface QualityRunClaimAuthorityV2 {
  runId: string;
  jobId: string;
  creationId: string;
  version: number;
  state: QualityRunStateV2;
}

/**
 * Validate the one complete claim tuple used by every verified-core@2 run
 * mutation. Possessing a live token for any other job is deliberately
 * insufficient: the admitted job id, creation/version tuple, required
 * capability, generation, expiry, and expected run revision must all match.
 */
export async function assertQualityRunClaim(
  tx: Pick<DocTxOps, 'get'>,
  run: QualityRunClaimAuthorityV2,
  claimValue: QualityClaimV1,
  now: number,
): Promise<QualityClaimV1> {
  if (run.state.runId !== run.runId || claimValue.jobId !== run.jobId) {
    throw new PlatformError('conflict', 'quality claim belongs to another admitted run');
  }
  const job = await tx.get<BuildJob>(buildJobDoc(run.jobId));
  if (!job || job.status !== 'claimed' || job.requiredWorkerCapability !== 'verified-core-v2'
    || job.creationId !== run.creationId || job.version !== run.version
    || !job.claimToken || !job.leaseGeneration || !job.leaseExpiry) {
    throw new PlatformError('conflict', 'quality claim does not match the admitted build tuple');
  }
  return assertQualityClaim(run.state, claimValue, {
    jobId: run.jobId,
    claimToken: job.claimToken,
    leaseGeneration: job.leaseGeneration,
    leaseExpiry: job.leaseExpiry,
    expectedRunRevision: run.state.revision,
  }, now);
}
